import { useState } from "react";
import { Play } from "lucide-react";
import { ytThumbnail, ytEmbedUrl } from "@/lib/youtube";
import { cdn } from "@/lib/img";

/**
 * Lite YouTube Embed — shows thumbnail + play button first,
 * only loads the heavy iframe player once the user clicks.
 *
 * Props:
 *   youtubeId: string (required)
 *   title: string (displayed + used for aria)
 *   customThumbnailUrl: optional override thumbnail
 *   aspectClass: tailwind class for aspect ratio wrapper (default "aspect-video")
 *   autoplay: if true, clicking auto-plays (default true)
 */
export default function YouTubeLite({ youtubeId, title, customThumbnailUrl, aspectClass = "aspect-video", autoplay = true }) {
  const [activated, setActivated] = useState(false);

  if (!youtubeId) return null;
  const rawThumb = customThumbnailUrl || ytThumbnail(youtubeId, "maxresdefault");
  // Route through our image CDN so dims are right & it loads fast
  const thumb = customThumbnailUrl ? cdn(customThumbnailUrl, 1024) : rawThumb;

  return (
    <div className={`relative overflow-hidden rounded-sm bg-black ${aspectClass}`} data-testid="youtube-lite">
      {!activated ? (
        <button
          type="button"
          onClick={() => setActivated(true)}
          aria-label={`Play video: ${title || "YouTube video"}`}
          className="group absolute inset-0 w-full h-full cursor-pointer"
        >
          <img
            src={thumb}
            alt={title || "Video thumbnail"}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            onError={(e) => {
              // Fallback to hqdefault if maxres isn't available
              if (!e.currentTarget.dataset.fallback) {
                e.currentTarget.dataset.fallback = "1";
                e.currentTarget.src = ytThumbnail(youtubeId, "hqdefault");
              }
            }}
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/50 group-hover:from-black/5 group-hover:to-black/40 transition-colors" />
          {/* Play button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-wc-forest/90 backdrop-blur-sm border border-wc-cream/30 shadow-2xl group-hover:bg-wc-teal group-hover:scale-110 transition-all duration-300">
              <Play className="w-6 h-6 sm:w-7 sm:h-7 text-wc-cream translate-x-0.5" fill="currentColor" />
            </span>
          </div>
          {/* Title at bottom */}
          {title && (
            <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
              <p className="font-accent italic text-wc-cream/90 text-sm sm:text-base drop-shadow">
                {title}
              </p>
            </div>
          )}
        </button>
      ) : (
        <iframe
          src={ytEmbedUrl(youtubeId, { autoplay })}
          title={title || "YouTube video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 w-full h-full border-0"
        />
      )}
    </div>
  );
}
